/**
 * Group Call Service
 * 
 * Handles group call creation, invitations, and lifecycle management.
 * Uses Firestore for real-time call state and invitations.
 * 
 * Collections:
 * - groupCalls: Active/ended group call sessions
 * - groupCallInvites: Individual invitations per user
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';

// ==========================================
// Types
// ==========================================

export interface GroupCall {
  id: string;
  hostUserId: string;
  hostUserName: string;
  hostUserPhoto?: string;
  roomId: string;
  callType: 'audio' | 'video';
  status: 'active' | 'ended';
  participants: string[];           // All invited user IDs (including host)
  participantNames: string[];       // Display names
  participantPhotos: string[];      // Photo URLs
  joinedParticipants: string[];     // Who actually joined
  createdAt: Timestamp;
  endedAt?: Timestamp;
}

export interface GroupCallInvite {
  id: string;
  groupCallId: string;
  roomId: string;
  callType: 'audio' | 'video';
  invitedUserId: string;
  invitedByUserId: string;
  invitedByUserName: string;
  invitedByUserPhoto?: string;
  participantNames: string[];       // All participant names (for display)
  status: 'pending' | 'accepted' | 'declined' | 'missed';
  createdAt: Timestamp;
}

export interface CallParticipant {
  uid: string;
  displayName: string;
  photoURL?: string;
}

// ==========================================
// Service
// ==========================================

class GroupCallService {
  private readonly CALLS_COLLECTION = 'groupCalls';
  private readonly INVITES_COLLECTION = 'groupCallInvites';

  /**
   * Create a new group call and send invitations to all participants
   */
  async createGroupCall(
    host: CallParticipant,
    invitedParticipants: CallParticipant[],
    callType: 'audio' | 'video'
  ): Promise<GroupCall> {
    try {
      // Generate unique room ID
      const roomId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;

      // All participants including host
      const allParticipants = [host, ...invitedParticipants];

      const callData = {
        hostUserId: host.uid,
        hostUserName: host.displayName,
        hostUserPhoto: host.photoURL || '',
        roomId,
        callType,
        status: 'active',
        participants: allParticipants.map(p => p.uid),
        participantNames: allParticipants.map(p => p.displayName),
        participantPhotos: allParticipants.map(p => p.photoURL || ''),
        joinedParticipants: [host.uid], // Host joins immediately
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, this.CALLS_COLLECTION), callData);
      console.log('✅ [GroupCall] Created group call:', docRef.id, 'room:', roomId);

      // Send invitations to all participants (except host)
      const invitePromises = invitedParticipants.map(participant =>
        this.sendInvite(docRef.id, roomId, callType, participant.uid, host, allParticipants.map(p => p.displayName))
      );
      await Promise.all(invitePromises);

      console.log(`✅ [GroupCall] Sent ${invitedParticipants.length} invitations`);

      return {
        id: docRef.id,
        ...callData,
        createdAt: Timestamp.now()
      } as GroupCall;
    } catch (error) {
      console.error('❌ [GroupCall] Error creating group call:', error);
      throw error;
    }
  }

  /**
   * Send a single invitation
   */
  private async sendInvite(
    groupCallId: string,
    roomId: string,
    callType: 'audio' | 'video',
    invitedUserId: string,
    host: CallParticipant,
    participantNames: string[]
  ): Promise<void> {
    const inviteData = {
      groupCallId,
      roomId,
      callType,
      invitedUserId,
      invitedByUserId: host.uid,
      invitedByUserName: host.displayName,
      invitedByUserPhoto: host.photoURL || '',
      participantNames,
      status: 'pending',
      createdAt: serverTimestamp()
    };

    await addDoc(collection(db, this.INVITES_COLLECTION), inviteData);
  }

  /**
   * Mark a user as joined
   */
  async joinGroupCall(groupCallId: string, userId: string): Promise<void> {
    try {
      const callRef = doc(db, this.CALLS_COLLECTION, groupCallId);
      await updateDoc(callRef, {
        joinedParticipants: arrayUnion(userId)
      });
      console.log('✅ [GroupCall] User joined:', userId);
    } catch (error) {
      console.error('❌ [GroupCall] Error joining group call:', error);
    }
  }

  /**
   * Accept an invitation
   */
  async acceptInvite(inviteId: string): Promise<void> {
    try {
      const inviteRef = doc(db, this.INVITES_COLLECTION, inviteId);
      await updateDoc(inviteRef, { status: 'accepted' });
      console.log('✅ [GroupCall] Invite accepted:', inviteId);
    } catch (error) {
      console.error('❌ [GroupCall] Error accepting invite:', error);
    }
  }

  /**
   * Decline an invitation
   */
  async declineInvite(inviteId: string): Promise<void> {
    try {
      const inviteRef = doc(db, this.INVITES_COLLECTION, inviteId);
      await updateDoc(inviteRef, { status: 'declined' });
      console.log('✅ [GroupCall] Invite declined:', inviteId);
    } catch (error) {
      console.error('❌ [GroupCall] Error declining invite:', error);
    }
  }

  /**
   * End a group call (host only)
   */
  async endGroupCall(groupCallId: string): Promise<void> {
    try {
      const callRef = doc(db, this.CALLS_COLLECTION, groupCallId);
      await updateDoc(callRef, {
        status: 'ended',
        endedAt: serverTimestamp()
      });

      // Mark all pending invites as missed
      const pendingInvitesQuery = query(
        collection(db, this.INVITES_COLLECTION),
        where('groupCallId', '==', groupCallId),
        where('status', '==', 'pending')
      );
      const pendingSnap = await getDocs(pendingInvitesQuery);
      const updatePromises = pendingSnap.docs.map(d =>
        updateDoc(d.ref, { status: 'missed' })
      );
      await Promise.all(updatePromises);

      console.log('✅ [GroupCall] Group call ended:', groupCallId);
    } catch (error) {
      console.error('❌ [GroupCall] Error ending group call:', error);
    }
  }

  /**
   * Get active group call by ID
   */
  async getGroupCall(groupCallId: string): Promise<GroupCall | null> {
    try {
      const callDoc = await getDoc(doc(db, this.CALLS_COLLECTION, groupCallId));
      if (!callDoc.exists()) return null;
      return { id: callDoc.id, ...callDoc.data() } as GroupCall;
    } catch (error) {
      console.error('❌ [GroupCall] Error getting group call:', error);
      return null;
    }
  }

  /**
   * Listen for incoming call invitations for a user (real-time)
   */
  listenForInvites(
    userId: string,
    onInvite: (invites: GroupCallInvite[]) => void
  ): () => void {
    const invitesQuery = query(
      collection(db, this.INVITES_COLLECTION),
      where('invitedUserId', '==', userId),
      where('status', '==', 'pending')
    );

    return onSnapshot(invitesQuery, (snapshot) => {
      const invites = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as GroupCallInvite[];

      // Only show invites from the last 2 minutes (ignore stale ones)
      const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
      const recentInvites = invites.filter(inv => {
        const createdAt = inv.createdAt?.toDate?.() || new Date(0);
        return createdAt > twoMinutesAgo;
      });

      onInvite(recentInvites);
    });
  }

  /**
   * Get accepted connections for a user (for the invite picker)
   */
  async getUserConnections(userId: string): Promise<CallParticipant[]> {
    try {
      // Query connections where user is user1 or user2
      const [q1Snap, q2Snap] = await Promise.all([
        getDocs(query(
          collection(db, 'connections'),
          where('user1Id', '==', userId),
          where('status', '==', 'accepted')
        )),
        getDocs(query(
          collection(db, 'connections'),
          where('user2Id', '==', userId),
          where('status', '==', 'accepted')
        ))
      ]);

      const otherUserIds: string[] = [];

      q1Snap.docs.forEach(d => {
        const data = d.data();
        if (data.user2Id) otherUserIds.push(data.user2Id);
      });
      q2Snap.docs.forEach(d => {
        const data = d.data();
        if (data.user1Id) otherUserIds.push(data.user1Id);
      });

      // Remove duplicates
      const uniqueIds = [...new Set(otherUserIds)];

      // Fetch user profiles
      const participants: CallParticipant[] = [];
      for (const uid of uniqueIds) {
        try {
          // Try userProfiles first
          const profileDoc = await getDoc(doc(db, 'userProfiles', uid));
          if (profileDoc.exists()) {
            const data = profileDoc.data();
            participants.push({
              uid,
              displayName: data.displayName || 'Onbekend',
              photoURL: data.photoURL || undefined
            });
          } else {
            // Fallback to users collection
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
              const data = userDoc.data();
              participants.push({
                uid,
                displayName: data.displayName || data.email?.split('@')[0] || 'Onbekend',
                photoURL: data.photoURL || undefined
              });
            }
          }
        } catch (err) {
          console.warn(`⚠️ [GroupCall] Could not load profile for ${uid}`);
        }
      }

      return participants.sort((a, b) => a.displayName.localeCompare(b.displayName));
    } catch (error) {
      console.error('❌ [GroupCall] Error loading connections:', error);
      return [];
    }
  }

  /**
   * Clean up old invites (older than 5 minutes)
   */
  async cleanupStaleInvites(userId: string): Promise<void> {
    try {
      const invitesQuery = query(
        collection(db, this.INVITES_COLLECTION),
        where('invitedUserId', '==', userId),
        where('status', '==', 'pending')
      );
      const snap = await getDocs(invitesQuery);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

      for (const d of snap.docs) {
        const createdAt = d.data().createdAt?.toDate?.() || new Date(0);
        if (createdAt < fiveMinutesAgo) {
          await updateDoc(d.ref, { status: 'missed' });
        }
      }
    } catch (error) {
      console.error('❌ [GroupCall] Error cleaning up invites:', error);
    }
  }
}

export const groupCallService = new GroupCallService();
